import { useCallback } from "react";
import {
    announceDropInColumn,
    announceDropToColumn,
    announceRefusedDrop,
    announceReturn,
    getCellTitle,
    getColumnTitle,
    type BoardAnnouncementMessage,
} from "./boardAnnouncements";
import type { BoardCells } from "./boardCells";
import type { BoardDragGroup } from "./boardDragGroup";
import {
    getDropOrder,
    getDropOutcome,
    getTargetRefusal,
    NO_LANDING_KEYS,
    type BoardDraggingState,
    type BoardDropLanding,
} from "./boardDragState";
import {
    announceGroupDrop,
    announceGroupRefusedDrop,
    announceGroupStay,
} from "./boardGroupAnnouncements";
import { resolveBoardGroupDrop } from "./boardGroupMove";
import { isPlacingInSort } from "./boardPlaceSpot";
import type {
    BoardRefusalAnnouncement,
    LatestBoardDragArgs,
} from "./useBoardDragStore";

interface UseBoardDropArgs<TItem> {
    latestRef: { readonly current: LatestBoardDragArgs<TItem> };
    announce: (
        cells: BoardCells<TItem>,
        placementKey: string,
        toMessage: BoardAnnouncementMessage,
    ) => void;
    announceRefusal: (refusal: BoardRefusalAnnouncement) => void;
    announceMessage: (message: string) => void;
}

/**
 * Commits a drop and announces it: one `onMove` for a card, one
 * `onMoveMany` for a group (with no moves when every card stayed but some
 * were refused; none when nothing moved or was refused). Returns where the
 * drop lands.
 */
export const useBoardDrop = <TItem>({
    latestRef,
    announce,
    announceRefusal,
    announceMessage,
}: UseBoardDropArgs<TItem>) => {
    const dropCard = useCallback(
        (current: BoardDraggingState): BoardDropLanding<TItem> => {
            const outcome = getDropOutcome(latestRef.current, current);
            const { move } = outcome;
            const refusal = getTargetRefusal(current);
            if (refusal !== undefined) {
                announceRefusal({
                    state: current,
                    reason: refusal,
                    toMessage: announceRefusedDrop,
                });
            } else if (move === null) {
                announce(outcome.cells, current.activeKey, announceReturn);
            } else {
                latestRef.current.onMove(move);
                announce(
                    outcome.cells,
                    current.activeKey,
                    move.from.columnId === move.to.columnId
                        ? announceDropInColumn
                        : announceDropToColumn,
                );
            }
            return outcome;
        },
        [announce, announceRefusal, latestRef],
    );

    const dropGroup = useCallback(
        (
            current: BoardDraggingState,
            group: BoardDragGroup,
        ): BoardDropLanding<TItem> => {
            const latestArgs = latestRef.current;
            const { placement } = current.target;
            const cellTitle = group.isLaneFixed
                ? getColumnTitle(latestArgs.columns, placement.columnId)
                : getCellTitle(latestArgs, placement);
            const refusal = getTargetRefusal(current);
            if (refusal !== undefined) {
                announceMessage(
                    announceGroupRefusedDrop({
                        count: group.itemIds.size,
                        cellTitle,
                        reason: refusal,
                    }),
                );
                return {
                    cells: latestArgs.cells,
                    focusKey: current.activeKey,
                    landingKeys: NO_LANDING_KEYS,
                };
            }
            const resolution = resolveBoardGroupDrop({
                ...latestArgs,
                group,
                activeKey: current.activeKey,
                target: current.target,
                compareItems: getDropOrder(latestArgs, current),
                isPlacedInSort: isPlacingInSort(latestArgs, current),
            });
            const { moves, refusals } = resolution.batch;
            if (moves.length > 0 || refusals.length > 0) {
                latestArgs.onMoveMany?.(resolution.batch);
            }
            announceMessage(
                moves.length === 0
                    ? announceGroupStay(group.itemIds.size, refusals)
                    : announceGroupDrop({
                          movedCount: moves.length,
                          refusals,
                          cellTitle,
                      }),
            );
            return resolution;
        },
        [announceMessage, latestRef],
    );

    return useCallback(
        (current: BoardDraggingState) =>
            current.group === null
                ? dropCard(current)
                : dropGroup(current, current.group),
        [dropCard, dropGroup],
    );
};
